import { useRef, useState } from "react";
import { Icon } from "../../components/ui/Icon";
import type { McpIntegrationSummary } from "@yazamco/shared-types";
import { CopilotApiError, mcpOauthStartUrl } from "../../lib/copilot";
import { useClickOutside } from "../../lib/useClickOutside";
import {
  useConnectMcpIntegration,
  useDisconnectMcpIntegration,
  useMcpIntegrations,
  useSetMcpIntegrationUse,
} from "./hooks";

/**
 * "Business tools": connect or disconnect your own accounts, then ask ordinary
 * questions. Tool policy belongs to the deployment, not to this popover.
 *
 * WHICH tools each business system exposes is not decided here. It is the
 * deployment's allowlist, administered in Settings → Business Integrations, and
 * a per-tool switch in the composer asked somebody to answer an
 * administrator's question in the middle of writing a sentence — for tools
 * whose names mean nothing without the server's documentation.
 *
 * This replaces the Phase 1 control, and the change is a change of MENTAL
 * MODEL rather than of layout. That control was
 *
 *     pick a server → pick a tool → fill a form → run → preview → attach → ask
 *
 * which made every single use of a business system a six-step errand, and made
 * the interesting question — "may Copilot read my Hudu?" — something you
 * answered again for every message. This one is
 *
 *     connect once → ask questions
 *
 * and everything after the second step is invisible. The manual path still
 * exists in the API for working out why an integration answers as it does; it
 * is deliberately not in the composer, because it was never the normal thing
 * anybody wanted to do.
 *
 * ── What this component can and cannot do ──────────────────────────────────
 *
 * It can start a connection, submit a personal token, and disconnect. That is
 * the whole surface, and it is the whole surface on
 * the server too: no endpoint reachable from here accepts an MCP endpoint, an
 * auth strategy, a tool allowlist, a read-only policy or an OAuth client id.
 * Adding an integration is a deploy. So there is no "Add server" button to
 * hide from a normal user — there is nothing behind it.
 *
 * It also never holds a credential. An OAuth connection is a top-level
 * navigation to the API, which redirects to the provider and exchanges the code
 * server-side; a personal token is submitted once and is never returned by any
 * endpoint again. What comes back here is connection status and account name.
 */

/** The `?businessTool=` status the OAuth callback redirects back with. */
function readCallbackStatus(): string | null {
  if (typeof window === "undefined") return null;
  const status = new URLSearchParams(window.location.search).get("businessTool");
  if (!status) return null;
  // Cleaned out of the address bar immediately. It is a one-time outcome, and
  // leaving it in the URL means a refresh re-reports a connection that
  // happened ten minutes ago.
  const url = new URL(window.location.href);
  url.searchParams.delete("businessTool");
  window.history.replaceState({}, "", url.toString());
  return status;
}

/**
 * What each outcome of the OAuth round-trip means, in the reader's terms.
 *
 * Every code the callback can redirect with is named here, and the ones that
 * are NOT named still surface their code. The three that used to be missing —
 * a failed token exchange above all — all collapsed into one generic sentence,
 * which meant the server knew exactly what had happened, said so in the query
 * string, and the browser threw it away. That turned a one-line fix (paste the
 * client secret) into a hunt through deployment logs.
 *
 * Showing the code is safe by construction: these are OUR identifiers, from a
 * closed set the API controls, and never a provider's own prose about our
 * request. `unknownCode` is what enforces that — the status arrives in a query
 * parameter, so anybody can put anything in it by handing somebody a link, and
 * rendering arbitrary text would make this banner a place to write in.
 */
function unknownCode(status: string): string | null {
  return /^[A-Z][A-Z0-9_]{2,63}$/.test(status) ? status : null;
}

function callbackMessage(status: string): { text: string; ok: boolean } {
  if (status === "connected") {
    return { text: "Business tool connected.", ok: true };
  }
  if (status === "COPILOT_MCP_OAUTH_DENIED") {
    return { text: "The connection was not completed.", ok: false };
  }
  if (status === "COPILOT_MCP_OAUTH_STATE_INVALID") {
    return {
      text: "That connection attempt had expired. Try connecting again.",
      ok: false,
    };
  }
  if (status === "COPILOT_MCP_OAUTH_NOT_CONFIGURED") {
    return {
      text: "This tool is not set up yet. Ask an administrator.",
      ok: false,
    };
  }
  // The token request reached the provider and came back refused. Almost
  // always the deployment's own OAuth app rather than anything this person
  // did — most often a client secret that is missing or no longer current,
  // because the consent screen does not need one and the token endpoint does.
  if (status === "COPILOT_MCP_OAUTH_FAILED") {
    return {
      text: "The connection could not be completed. Ask an administrator to check this tool's OAuth client secret.",
      ok: false,
    };
  }
  // The provider refused the deployment's OAuth app. Reconnecting cannot help,
  // so the message does not suggest it.
  if (status === "COPILOT_MCP_OAUTH_CLIENT_REJECTED") {
    return {
      text: "This tool's OAuth app was refused. Ask an administrator to check its client id and client secret.",
      ok: false,
    };
  }
  if (status === "COPILOT_MCP_OAUTH_INVALID_GRANT") {
    return {
      text: "That connection was refused. Try connecting again.",
      ok: false,
    };
  }
  if (status === "COPILOT_MCP_AUTO_DISCOVERY_FAILED") {
    return {
      text: "The account connected, but its tools could not be configured automatically. Try reconnecting or ask an administrator.",
      ok: false,
    };
  }
  if (status === "COPILOT_MCP_NOT_OAUTH") {
    return {
      text: "This tool no longer connects this way. Try connecting again.",
      ok: false,
    };
  }
  if (status === "COPILOT_MCP_INTEGRATION_NOT_FOUND") {
    return { text: "That business tool is no longer available.", ok: false };
  }
  const code = unknownCode(status);
  return {
    text: code
      ? `The connection could not be completed (${code}).`
      : "The connection could not be completed.",
    ok: false,
  };
}

export interface BusinessToolsState {
  /** False on a deployment with no integrations: the control is absent, not disabled. */
  visible: boolean;
  items: McpIntegrationSummary[];
  /** Connected integrations this user has switched on — the composer's chips. */
  activeItems: McpIntegrationSummary[];
  /** How many business systems are switched on. The composer's badge. */
  activeSources: number;
  isLoading: boolean;
  isError: boolean;
  isFetched: boolean;
}

/**
 * What the composer needs to draw its Tools button and the chips in the
 * active-context row, without opening the popover.
 *
 * Fetched eagerly, and it has to be: the control HIDES ITSELF on a deployment
 * with no integrations, and it cannot know that without asking. Gating this on
 * the popover being open would leave a button on every page that disappears
 * the moment somebody clicks it.
 */
export function useBusinessTools(): BusinessToolsState {
  const integrations = useMcpIntegrations();
  const items = integrations.data?.items ?? [];
  const configured = integrations.data?.enabled ?? false;
  const activeItems = items.filter((item) => item.connected && item.enabledToolCount > 0);

  return {
    visible: !(integrations.isFetched && !configured),
    items,
    activeItems,
    activeSources: activeItems.length,
    isLoading: integrations.isLoading,
    isError: integrations.isError,
    isFetched: integrations.isFetched,
  };
}

/**
 * "Business tools": connect or disconnect your own accounts, then ask ordinary
 * questions. The deployment controls which safe tools Copilot may use.
 *
 * This is the popover CONTENT only. The trigger, the click-outside and the
 * placement belong to the composer, because the canvas draws one popover well
 * for all five composer controls rather than five that each invent their own.
 */
export function BusinessToolsPanel(): JSX.Element {
  const [tokenFor, setTokenFor] = useState<string | null>(null);
  const { items, isLoading, isError, isFetched } = useBusinessTools();
  const [callback] = useState(readCallbackStatus);

  return (
    <>
      <div className="cp-pop-head">
        <strong>Business tools</strong>
      </div>
      <div className="cp-pop-scroll">
        {callback && (
          <p
            className={callbackMessage(callback).ok ? "cp-pop-note" : "cp-pop-error"}
            role={callbackMessage(callback).ok ? undefined : "alert"}
          >
            {callbackMessage(callback).text}
          </p>
        )}

        {isLoading && <p className="cp-pop-hint">Loading tools…</p>}
        {isError && <p className="cp-pop-hint">The business tools could not be loaded.</p>}
        {items.length === 0 && isFetched && <p className="cp-pop-hint">No business tools yet.</p>}

        {items.map((item) =>
          item.protocol === "text-webhook" ? (
            <WebhookIntegrationRow key={item.id} integration={item} />
          ) : (
            <IntegrationRow
              key={item.id}
              integration={item}
              tokenFormOpen={tokenFor === item.id}
              onTokenConnect={() => {
                setTokenFor(item.id);
              }}
              onCloseTokenForm={() => setTokenFor(null)}
            />
          ),
        )}
      </div>
    </>
  );
}

/**
 * A text webhook's enable switch.
 *
 * Webhooks do not have a personal account to connect or disconnect, so this is
 * the only integration type that keeps a use toggle in the composer.
 */
function UseIntegrationSwitch({
  integration,
}: {
  integration: McpIntegrationSummary;
}): JSX.Element {
  const setUse = useSetMcpIntegrationUse();
  const [error, setError] = useState<string | null>(null);
  const enabled = integration.enabledToolCount > 0;
  const id = `cp-bt-use-${integration.id}`;

  return (
    <>
      <span className="cp-bt-use">
        <input
          className="cp-bt-use-toggle"
          id={id}
          type="checkbox"
          role="switch"
          aria-label={`Use ${integration.displayName}`}
          checked={enabled}
          // Switching OFF is never blocked by the state of anything else, so
          // only a pending request and a deployment-disabled integration
          // disable the control — and the latter cannot be on in the first place.
          disabled={setUse.isPending || (!integration.enabled && !enabled)}
          onChange={(event) => {
            setError(null);
            setUse.mutate(
              { integrationId: integration.id, enabled: event.target.checked },
              {
                onError: (failure) =>
                  setError(
                    failure instanceof CopilotApiError || failure instanceof Error
                      ? failure.message
                      : `${integration.displayName} could not be switched on.`,
                  ),
              },
            );
          }}
        />
      </span>
      {error && (
        <small className="cp-bs-error" role="alert">
          {error}
        </small>
      )}
    </>
  );
}

/** A text webhook has no user-facing configuration: its one toggle is the UI. */
function WebhookIntegrationRow({
  integration,
}: {
  integration: McpIntegrationSummary;
}): JSX.Element {
  return (
    <div className="cp-bt-row">
      {integration.icon?.startsWith("https://") ? (
        <img
          src={integration.icon}
          alt=""
          width={24}
          height={24}
          style={{ objectFit: "contain" }}
          referrerPolicy="no-referrer"
        />
      ) : (
        <i
          className={`fas ${integration.icon ? `fa-${integration.icon}` : "fa-database"}`}
          aria-hidden
        />
      )}
      <span className="cp-bt-row-text">
        <strong>{integration.displayName}</strong>
        <small>Source mixing follows your Copilot settings.</small>
      </span>
      <UseIntegrationSwitch integration={integration} />
    </div>
  );
}

/** Why an integration cannot be used at all. */
function blockedReason(integration: McpIntegrationSummary): string | null {
  if (!integration.enabled) return "Switched off";
  if (!integration.readOnly) return "Not available — this tool can change data";
  return null;
}

/**
 * Whether connecting an account is possible at all.
 *
 * Deliberately NOT `blockedReason(integration) === null`, and the difference is
 * the onboarding flow: connect → discover → approve tools → enable. `enabled`
 * gates tool USE and is the LAST step, so an integration that is switched off
 * is precisely the one somebody still has to connect — hiding the button there
 * makes the first step reachable only after the last one.
 *
 * `readOnly` is different and still hides it. Every call to a non-read-only
 * integration is refused at the server, so a connection would make nothing
 * possible, and offering a button whose outcome is a refusal is worse than
 * showing the reason.
 */
function canConnect(integration: McpIntegrationSummary): boolean {
  return integration.readOnly;
}

/**
 * One row: what it is, where the connection stands, and the one thing to do.
 *
 * The status line is the whole point of the row. "Not connected" and
 * "Connection expired" lead to the same button doing the same thing, and they
 * are still different sentences, because one of them means somebody's grant
 * lapsed and the other means they never had one — and a person who reads
 * "Connect" on a tool they connected last month will reasonably wonder what
 * happened to it.
 */
function IntegrationRow({
  integration,
  tokenFormOpen,
  onTokenConnect,
  onCloseTokenForm,
}: {
  integration: McpIntegrationSummary;
  tokenFormOpen: boolean;
  onTokenConnect: () => void;
  onCloseTokenForm: () => void;
}): JSX.Element {
  const blocked = blockedReason(integration);
  const status = statusLine(integration);
  const disconnect = useDisconnectMcpIntegration();
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useClickOutside(menuRef, menuOpen, () => setMenuOpen(false));

  const connect = (): void => {
    if (integration.authKind === "oauth2-user") {
      // A TOP-LEVEL navigation, not a fetch. The API answers with a redirect to
      // the provider; the code comes back to the API's own callback and is
      // exchanged there. No token passes through this file.
      window.location.assign(mcpOauthStartUrl(integration.id, "/copilot"));
      return;
    }
    onTokenConnect();
  };

  return (
    <>
      <div className={`cp-bt-row${blocked ? " is-blocked" : ""}`}>
        {integration.icon?.startsWith("https://") ? (
          <img
            src={integration.icon}
            alt=""
            width={24}
            height={24}
            style={{ objectFit: "contain" }}
            referrerPolicy="no-referrer"
          />
        ) : (
          <i
            className={`fas ${integration.icon ? `fa-${integration.icon}` : "fa-database"}`}
            aria-hidden
          />
        )}
        <span className="cp-bt-row-text">
          <strong>{integration.displayName}</strong>
          {(blocked || !integration.connected || !integration.requiresUserConnection) && (
            <small>{blocked ?? status}</small>
          )}
        </span>
        <span className="cp-bt-row-actions">
          {integration.connected ? (
            <UseIntegrationSwitch integration={integration} />
          ) : (
            !tokenFormOpen && canConnect(integration) && (
              <button
                type="button"
                className="cp-bt-action is-primary"
                onClick={connect}
              >
                Connect
              </button>
            )
          )}

          {integration.requiresUserConnection && canConnect(integration) && (
            <div className={`cp-bt-more${menuOpen ? " is-open" : ""}`} ref={menuRef}>
              <button
                type="button"
                className="cp-bt-more-trigger"
                aria-label={`${integration.displayName} actions`}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((open) => !open)}
              >
                <Icon name="dots" size={14} />
              </button>
              {menuOpen && (
                <div className="cp-bt-menu" role="menu">
                  {integration.connected ? (
                    <button
                      type="button"
                      role="menuitem"
                      className="menu-item menu-item-danger"
                      disabled={disconnect.isPending}
                      onClick={() => {
                        setMenuOpen(false);
                        setError(null);
                        disconnect.mutate(integration.id, {
                          onError: () =>
                            setError(
                              `${integration.displayName} could not be disconnected. Try again.`,
                            ),
                        });
                      }}
                    >
                      {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      role="menuitem"
                      className="menu-item"
                      onClick={() => {
                        setMenuOpen(false);
                        connect();
                      }}
                    >
                      Reconnect
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </span>
      </div>
      {tokenFormOpen && (
        <div className="cp-bt-connect-form">
          <TokenConnectForm
            integration={integration}
            onDone={onCloseTokenForm}
            onCancel={onCloseTokenForm}
          />
        </div>
      )}
      {error && (
        <small className="cp-bs-error" role="alert">
          {error}
        </small>
      )}
    </>
  );
}

function statusLine(integration: McpIntegrationSummary): string {
  if (!integration.requiresUserConnection) {
    return integration.authKind === "service-account"
      ? "Shared account — the same for everyone"
      : integration.description ?? "No sign-in needed";
  }
  switch (integration.connectionStatus) {
    case "connected":
      return integration.accountDisplayName
        ? `Connected as ${integration.accountDisplayName}`
        : "Connected";
    case "expired":
      return "Connection expired";
    case "revoked":
      return "Connection was rejected";
    default:
      return integration.description ?? "Not connected";
  }
}

/**
 * The personal-token form.
 *
 * A password field, submitted once. There is no "show saved token" and no
 * pre-filled value, because there is no endpoint that would return one: the
 * token is encrypted on arrival and the only thing any API will say about it
 * afterwards is that a connection exists. Re-submitting REPLACES it.
 */
function TokenConnectForm({
  integration,
  onDone,
  onCancel,
}: {
  integration: McpIntegrationSummary;
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const connect = useConnectMcpIntegration();

  return (
    <form
      className="cp-bs-form"
      onSubmit={(event) => {
        event.preventDefault();
        const value = token.trim();
        if (value.length < 8) {
          setError("Enter your token.");
          return;
        }
        setError(null);
        connect.mutate(
          { integrationId: integration.id, token: value },
          {
            onSuccess: () => {
              // Cleared from component state the moment it is accepted. It
              // lives on as ciphertext on the server and nowhere else.
              setToken("");
              onDone();
            },
            onError: (failure) =>
              setError(
                failure instanceof CopilotApiError || failure instanceof Error
                  ? failure.message
                  : "The token could not be saved.",
              ),
          },
        );
      }}
    >
      <div className="cp-bs-field">
        <label htmlFor="cp-bt-token">
          {integration.connected ? "Replace the saved token" : "Personal API token"}
        </label>
        <input
          id="cp-bt-token"
          type="password"
          autoComplete="off"
          spellCheck={false}
          maxLength={4_096}
          value={token}
          onChange={(event) => setToken(event.target.value)}
        />
        <small>
          Copilot reads {integration.displayName} as you, through tools approved
          by your administrator.
        </small>
      </div>
      {error && (
        <p className="cp-bs-error" role="alert">
          {error}
        </p>
      )}
      <div className="cp-bt-form-actions">
        <button type="submit" className="cp-bs-run" disabled={connect.isPending}>
          {connect.isPending ? "Saving…" : "Connect"}
        </button>
        <button type="button" className="cp-bt-action" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
